import { formatDistanceToNow } from 'date-fns';
import { Fragment, type MouseEvent, type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';

import {
  useInfiniteNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationUnreadCount
} from '@global-hooks/notifications';
import useWebSocket from '@global-hooks/webSocket';
import { useAuth } from '@global-contexts/auth';
import type Notification from '@global-interfaces/Notification';
import type { NotificationSeverity } from '@global-interfaces/Notification';

type Freshness = 'connecting' | 'live' | 'degraded';

const severityColors: Record<NotificationSeverity, string> = {
  info: '#59afd4',
  success: '#0acf97',
  warning: '#f6c344',
  error: '#fa5c7c'
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isPayoutNotificationFrame = (data: unknown) => {
  if (!isRecord(data) || !isRecord(data.Received) || !isRecord(data.Received.message)) {
    return false;
  }
  return data.Received.message.sender === 'PayoutNotification';
};

const formatCreatedAt = (createdAt: string) => formatDistanceToNow(new Date(createdAt), { addSuffix: true });

const paymentNotificationEvents = new Set([
  'payment_pending',
  'payment_completed',
  'payment_failed',
  'payment_canceled'
]);
const bankAccountNotificationEvents = new Set(['bank_account_created', 'bank_account_updated']);
const vendorBankNotificationEvents = new Set(['vendor_bank_proposal_review_required', 'bank_destination_changed']);
const safeVendorAction = /^\/settings\/vendors\/edit\/\d+$/;
const emailSyncAuthenticationEvent = 'email_sync_authentication_required';

export const getNotificationDestination = (notification: Notification, isAdmin: boolean) => {
  if (
    vendorBankNotificationEvents.has(notification.event_type) &&
    notification.action_url &&
    safeVendorAction.test(notification.action_url)
  ) {
    return notification.action_url;
  }
  if (!isAdmin) {
    return null;
  }
  if (paymentNotificationEvents.has(notification.event_type)) {
    if (notification.transaction_id) {
      const searchParams = new URLSearchParams({
        transactionId: notification.transaction_id.toString(),
        notificationId: notification.id.toString()
      });
      return `/payment-transactions?${searchParams.toString()}`;
    }
    return '/payment-transactions';
  }
  if (bankAccountNotificationEvents.has(notification.event_type)) {
    return '/settings/payment-methods/bank-accounts';
  }
  if (notification.event_type === emailSyncAuthenticationEvent) {
    return '/settings/email-forwarding';
  }
  return null;
};

const MixedPaymentReviewBody = ({ review }: { review: NonNullable<Notification['mixed_payment_review']> }) => {
  const invoiceLinks = (invoices: { id: number | null; number: string | null }[]) =>
    invoices.map((invoice, index) => (
      <Fragment key={invoice.id ?? `unavailable-${index}`}>
        {index > 0 && ', '}
        {invoice.id && Number.isSafeInteger(invoice.id) && invoice.id > 0 ? (
          <Link component={RouterLink} to={`/invoice/${invoice.id}`}>
            <strong>{invoice.number || invoice.id}</strong>
          </Link>
        ) : (
          <strong>{invoice.number || 'Unavailable historical invoice'}</strong>
        )}
      </Fragment>
    ));
  return (
    <Stack spacing={1}>
      <Typography component="p" variant="body2" color="grey.700">
        <Link
          href={`https://app.qbo.intuit.com/app/billpayment?txnId=${encodeURIComponent(review.remote_payment_id)}`}
          target="_blank"
          rel="noopener noreferrer">
          <strong>QuickBooks payment {review.remote_payment_id}</strong>
        </Link>{' '}
        is on hold in inBuild because it combines invoices from the previous payment system (
        {invoiceLinks(review.groups.legacy)}) and the new payment system ({invoiceLinks(review.groups.canonical)}).
        {!!review.previously_linked_invoices?.length && (
          <> Previously linked invoices still on hold: {invoiceLinks(review.previously_linked_invoices)}.</>
        )}
      </Typography>
      <Typography component="p" variant="body2" color="grey.700">
        Do not pay again. The payment is already recorded in QuickBooks. Correct its invoice allocations in QuickBooks
        so old and new invoices are in separate batches, then click Sync Payments.
      </Typography>
    </Stack>
  );
};

const getDestinationLabel = (destination: string | null) => {
  if (destination === '/payment-transactions') {
    return 'Payment Transactions';
  }
  if (destination === '/settings/payment-methods/bank-accounts') {
    return 'Bank Accounts';
  }
  if (destination?.startsWith('/settings/vendors/edit/')) {
    return 'Vendor payout destination';
  }
  if (destination === '/settings/email-forwarding') {
    return 'Email Forwarding';
  }
  return null;
};

interface NotificationsBellContentProps {
  socket?: WebSocket;
}

export const NotificationsBellContent = ({ socket }: NotificationsBellContentProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [freshness, setFreshness] = useState<Freshness>(() => {
    if (!socket) {
      return 'degraded';
    }
    return socket.readyState === WebSocket.OPEN ? 'live' : 'connecting';
  });
  const reconcileTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchingNextPage = useRef(false);
  const notifications = useInfiniteNotifications(
    {},
    { refetchInterval: freshness === 'degraded' && open ? 30_000 : false }
  );
  const unreadCount = useNotificationUnreadCount({
    refetchInterval: freshness === 'degraded' ? 30_000 : false
  });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const unread = unreadCount.data?.unread ?? 0;
  const notificationItems = useMemo(() => {
    const uniqueNotifications = new Map<number, Notification>();
    notifications.data?.pages.forEach(({ results }) => {
      results.forEach((notification) => uniqueNotifications.set(notification.id, notification));
    });
    return [...uniqueNotifications.values()];
  }, [notifications.data?.pages]);
  const handleNotificationScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const list = event.currentTarget;
      const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
      if (
        distanceFromBottom <= 80 &&
        notifications.hasNextPage &&
        !notifications.isFetchingNextPage &&
        !fetchingNextPage.current
      ) {
        fetchingNextPage.current = true;
        void notifications.fetchNextPage().finally(() => {
          fetchingNextPage.current = false;
        });
      }
    },
    [notifications.fetchNextPage, notifications.hasNextPage, notifications.isFetchingNextPage]
  );
  const reconcile = useCallback(() => {
    if (reconcileTimer.current) {
      return;
    }
    reconcileTimer.current = setTimeout(() => {
      reconcileTimer.current = null;
      void Promise.all([notifications.refetch(), unreadCount.refetch()]);
    }, 750);
  }, [notifications.refetch, unreadCount.refetch]);

  useEffect(
    () => () => {
      if (reconcileTimer.current) {
        clearTimeout(reconcileTimer.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!socket) {
      setFreshness('degraded');
      return;
    }

    const handleOpen = () => {
      setFreshness('live');
      reconcile();
    };
    const handleDegraded = () => {
      setFreshness('degraded');
      reconcile();
    };
    const handleMessage = (event: MessageEvent) => {
      try {
        const data: unknown = JSON.parse(String(event.data));
        if (isPayoutNotificationFrame(data)) {
          reconcile();
        }
      } catch {
        return;
      }
    };

    socket.addEventListener('open', handleOpen);
    socket.addEventListener('message', handleMessage);
    socket.addEventListener('close', handleDegraded);
    socket.addEventListener('error', handleDegraded);

    if (socket.readyState === WebSocket.OPEN) {
      handleOpen();
    } else if (socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
      handleDegraded();
    }

    return () => {
      socket.removeEventListener('open', handleOpen);
      socket.removeEventListener('message', handleMessage);
      socket.removeEventListener('close', handleDegraded);
      socket.removeEventListener('error', handleDegraded);
    };
  }, [reconcile, socket]);

  const closeMenu = () => setAnchorEl(null);
  const retry = () => {
    void Promise.all([notifications.refetch(), unreadCount.refetch()]);
  };

  return (
    <>
      <IconButton
        aria-label={`${unread} unread notification${unread === 1 ? '' : 's'}`}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        data-tooltip-id="topbar-payment-notifications"
        onClick={(event: MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
          reconcile();
        }}>
        <Badge badgeContent={unread} max={99} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Tooltip id="topbar-payment-notifications" variant="dark" delayShow={400}>
        <span>Notifications</span>
      </Tooltip>

      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          'aria-label': 'Notifications',
          'data-testid': 'payment-notification-list',
          onScroll: handleNotificationScroll,
          style: {
            width: 'min(360px, calc(100vw - 16px))',
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'min(600px, calc(100dvh - 96px))',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            paddingBottom: 0,
            paddingTop: 0
          }
        }}>
        <ListSubheader
          component="div"
          data-testid="payment-notification-header"
          sx={{
            bgcolor: 'background.paper',
            lineHeight: 1.4,
            minHeight: 52,
            position: 'sticky',
            px: 2,
            py: 1,
            top: 0,
            zIndex: 3
          }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Notifications
          </Typography>
          <Typography variant="caption" color="grey.700">
            {freshness === 'live' ? 'Live updates' : freshness === 'connecting' ? 'Connecting' : 'REST updates'}
          </Typography>
        </ListSubheader>
        <MenuItem
          disabled={unread === 0 || markAllRead.isPending || markRead.isPending}
          onClick={() => markAllRead.mutate()}
          sx={{
            bgcolor: 'background.paper',
            minHeight: 48,
            position: 'sticky',
            top: 52,
            zIndex: 3,
            '&.Mui-disabled': {
              bgcolor: 'background.paper',
              opacity: 1
            }
          }}>
          <Typography variant="body2" color={unread === 0 ? 'text.disabled' : 'primary.main'} fontWeight={600}>
            {markAllRead.isPending ? 'Marking as read…' : unread === 0 ? 'All notifications read' : 'Mark all as read'}
          </Typography>
        </MenuItem>
        <Divider sx={{ bgcolor: 'background.paper', m: 0, position: 'sticky', top: 100, zIndex: 3 }} />

        {notifications.isLoading || unreadCount.isLoading ? (
          <MenuItem disabled sx={{ justifyContent: 'center', minHeight: 88 }}>
            <CircularProgress size={20} aria-label="Loading notifications" />
          </MenuItem>
        ) : notifications.isError || unreadCount.isError ? (
          <MenuItem onClick={retry} sx={{ justifyContent: 'center', py: 2 }}>
            <Stack alignItems="center" spacing={0.5}>
              <Typography variant="body2">Notifications could not be loaded.</Typography>
              <Typography variant="body2" color="primary.main" fontWeight={600}>
                Retry
              </Typography>
            </Stack>
          </MenuItem>
        ) : notificationItems.length ? (
          notificationItems.map((notification) => {
            const destination = getNotificationDestination(notification, Boolean(user?.isAdmin));
            const destinationLabel = getDestinationLabel(destination);
            const mixedReview =
              notification.event_type === 'qbo_bill_payment_review' && notification.mixed_payment_review;
            return (
              <MenuItem
                key={notification.id}
                disabled={markAllRead.isPending}
                onKeyDown={(event) => {
                  // Let keyboard users reach the inline links before leaving this notification.
                  if (mixedReview && event.key === 'Tab') {
                    event.stopPropagation();
                  }
                }}
                aria-label={`${notification.read_at ? 'Read' : 'Unread'} ${notification.severity} notification: ${notification.title}. ${notification.body}. ${formatCreatedAt(notification.created_at)}.${notification.read_at ? '' : ' Activate to mark as read.'}${destinationLabel ? ` Opens ${destinationLabel}.` : ''}`}
                onClick={() => {
                  if (!notification.read_at) {
                    markRead.mutate(notification.id);
                  }
                  if (destination) {
                    closeMenu();
                    if (destination.startsWith('/admin/')) {
                      const backendOrigin = import.meta.env.VITE_BACKEND_HOST || window.location.origin;
                      window.location.assign(new URL(destination, backendOrigin).toString());
                    } else {
                      navigate(destination);
                    }
                  }
                }}
                sx={{
                  alignItems: 'stretch',
                  gap: 1.25,
                  py: 1.25,
                  bgcolor: notification.read_at ? 'transparent' : 'rgba(89, 175, 212, 0.1)',
                  whiteSpace: 'normal'
                }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    bgcolor: severityColors[notification.severity],
                    borderRadius: 2,
                    flex: '0 0 4px'
                  }}
                />
                <Stack minWidth={0} flex={1} spacing={0.25}>
                  <Typography variant="body2" fontWeight={notification.read_at ? 500 : 700}>
                    {notification.title}
                  </Typography>
                  {mixedReview ? (
                    <MixedPaymentReviewBody review={mixedReview} />
                  ) : (
                    <Typography variant="body2" color="grey.700" sx={{ whiteSpace: 'pre-line' }}>
                      {notification.body}
                    </Typography>
                  )}
                  <Typography variant="caption" color="grey.600">
                    {formatCreatedAt(notification.created_at)}
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled sx={{ justifyContent: 'center', py: 2.5 }}>
            <Stack alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight={600}>
                No notifications
              </Typography>
              <Typography variant="caption" color="grey.700" textAlign="center">
                New updates will appear here.
              </Typography>
            </Stack>
          </MenuItem>
        )}

        {notifications.isFetchingNextPage && (
          <Box display="flex" justifyContent="center" py={1}>
            <CircularProgress size={18} aria-label="Loading older notifications" />
          </Box>
        )}

        {(markRead.isError || markAllRead.isError) && (
          <ListSubheader component="div" role="alert" sx={{ lineHeight: 1.4, px: 2, py: 1 }}>
            <Typography variant="caption" color="error.main">
              The notification could not be updated. Try again.
            </Typography>
          </ListSubheader>
        )}
      </Menu>
    </>
  );
};

const SocketNotificationsBell = () => {
  const { exportWebSocket } = useWebSocket('payment-notifications');
  return <NotificationsBellContent socket={exportWebSocket} />;
};

const NotificationsBell = () => {
  if (!import.meta.env.VITE_WS_SERVER) {
    return <NotificationsBellContent />;
  }
  return <SocketNotificationsBell />;
};

export default NotificationsBell;
